import { Helmet } from 'react-helmet-async';

const siteUrl = 'https://msp-vergelijker.nl';
const canonicalSiteUrl = 'https://msp-vergelijker.nl';

export function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "MSP-Vergelijker",
        "url": canonicalSiteUrl,
        "logo": `${canonicalSiteUrl}/email/msp-vergelijker-logo.png`,
        "description": "Onafhankelijk platform dat MKB-bedrijven helpt bij het vergelijken en selecteren van Managed Service Providers (MSP's) in Nederland.",
        "sameAs": [
          "https://www.linkedin.com/company/msp-vergelijker"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "email": "info@msp-vergelijker.nl"
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "NL"
        }
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": canonicalSiteUrl,
        "name": "MSP-Vergelijker",
        "publisher": { "@id": `${siteUrl}/#organization` },
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${canonicalSiteUrl}/vergelijken?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface ItemListSchemaProps {
  items: Array<{
    name: string;
    url: string;
    image?: string;
    description?: string;
  }>;
  name?: string;
}

export function ItemListSchema({ items, name = "MSP Overzicht" }: ItemListSchemaProps) {
  // Filter out items with missing name or url — invalid for schema.org ItemList
  const validItems = items.filter(
    (item) => item && item.name && item.name.trim() && item.url && item.url.trim()
  );
  // Ensure URLs are absolute using the canonical site URL
  const toAbsolute = (url: string) => (url.startsWith('http') ? url : `${canonicalSiteUrl}${url.startsWith('/') ? '' : '/'}${url}`);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": name,
    "numberOfItems": validItems.length,
    "itemListElement": validItems.map((item, index) => {
      const absoluteUrl = toAbsolute(item.url);
      return {
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Organization",
          "@id": absoluteUrl,
          "name": item.name,
          "url": absoluteUrl,
          ...(item.image && { "image": toAbsolute(item.image) }),
          ...(item.description && { "description": item.description })
        }
      };
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface ArticleSchemaProps {
  headline: string;
  description: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  url: string;
}

export function ArticleSchema({ 
  headline, 
  description, 
  image, 
  datePublished, 
  dateModified,
  author = "MSP-Vergelijker.nl",
  url 
}: ArticleSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "description": description,
    "url": url,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "MSP-Vergelijker.nl",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/email/msp-vergelijker-logo.png`
      }
    },
    ...(image && { 
      "image": {
        "@type": "ImageObject",
        "url": image
      }
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface FAQSchemaProps {
  faqs: Array<{
    question: string;
    answer: string;
  }>;
}

export function FAQSchema({ faqs }: FAQSchemaProps) {
  // Filter out empty Q/A — invalid for schema.org FAQPage
  const validFaqs = (faqs || []).filter(
    (f) => f && f.question && f.question.trim() && f.answer && f.answer.trim()
  );
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": validFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
